import * as constants from "./constants"
import { Channel, ChannelsResponse } from "../../models/channel"
import channelsController from "../../api/controllers/channels"
import { ChannelDeclarations } from "../../models/parameterDeclaration"
import { mapChannelDtoToChannel, mapChannelToChannelDto } from "../../utility/channels/channelTranslator"
import { actions } from "./slice"
import { handleHttpException } from "../handleHttpException"
import { apiCallExceptionToSystemError } from "../../utility/common/apiCallExceptionToSystemError"
import { logError } from "../../utility/common/logError"
import { saveNotificationError } from "../notifications/thunks"
import { channelsHub } from "../../api/instances/channelsHub"
import { Controller } from "../../api/fake/controller"
import { selectCurrentProjectId } from "../projects/selectors"
import store, { AppDispatch } from "../store"
import { updateChannelsCache } from "../../api/controllers/channels/helpers"
import { channelsApi } from "../../api/controllers/channels/controller"
import { getChannelsTag, getWebChatAdditionalDataTag } from "../../api/tags"
import { FullTagDescription } from "@reduxjs/toolkit/dist/query/endpointDefinitions"

type ChannelTag = FullTagDescription<string> & { projectId: string }

// TODO: временный workaround до миграции на RTK Query mutations (CRAFT-219)
const invalidateChannelsTags = (dispatch: AppDispatch, projectId: string) => {
    const tags: ChannelTag[] = [
        { type: getChannelsTag, projectId },
        { type: getWebChatAdditionalDataTag, projectId }
    ]
    dispatch(channelsApi.util.invalidateTags(tags))
}

const updateChannels =
    (dispatch: AppDispatch) => (channelToAdd: Channel | undefined, channelToRemove: Channel | undefined) => {
        const state = store.getState()
        const currentProjectId = selectCurrentProjectId(state)

        if (currentProjectId) {
            updateChannelsCache(dispatch, currentProjectId, (draft: ChannelsResponse) => {
                if (channelToAdd && channelToRemove && channelToRemove.Id === channelToAdd.Id) {
                    draft.channels = draft.channels.map((c: Channel) => (c.Id === channelToAdd.Id ? channelToAdd : c))
                    return
                }
                if (channelToRemove) {
                    draft.channels = draft.channels.filter((c: Channel) => c.Id !== channelToRemove.Id)
                }
                if (channelToAdd) {
                    draft.channels.push(channelToAdd)
                }
            })
        }
    }

export const subscribeOnChannelsReady = (projectId: string) => async (dispatch: AppDispatch) => {
    try {
        await channelsHub.subscribe(projectId)
    } catch (e) {
        const error = apiCallExceptionToSystemError(constants.SUBSCRIBE_ON_CHANNELS_READY_FAILED, e)
        logError(error)
        saveNotificationError(dispatch, error)
    }
}

export const unsubscribeOnChannelsReady = (projectId: string) => async (dispatch: AppDispatch) => {
    try {
        await channelsHub.unsubscribe(projectId)
    } catch (e) {
        const error = apiCallExceptionToSystemError(constants.UNSUBSCRIBE_ON_CHANNELS_READY_FAILED, e)
        logError(error)
        saveNotificationError(dispatch, error)
    }
}

export function createChannel(
    channel: Channel,
    declarations: ChannelDeclarations,
    callback: (channel: Channel) => void
) {
    return async (dispatch: AppDispatch) => {
        try {
            dispatch(actions.createChannelProcess())

            const createdChannel = await channelsController.create(mapChannelToChannelDto(channel))
            const processedChannel = mapChannelDtoToChannel(createdChannel, declarations)

            dispatch(actions.createChannelSuccess())
            updateChannels(dispatch)(processedChannel, undefined)
            const state = store.getState()
            const currentProjectId = selectCurrentProjectId(state)
            if (currentProjectId) {
                invalidateChannelsTags(dispatch, currentProjectId)
            }
            callback(channel)
        } catch (e) {
            handleHttpException(
                e,
                constants.CREATE_CHANNEL_FAILED_MESSAGE,
                err => actions.createChannelFailed(err),
                dispatch
            )
        }
    }
}

export function updateChannel(
    channel: Channel,
    declarations: ChannelDeclarations,
    callback: (channel: Channel) => void
) {
    return async (dispatch: AppDispatch) => {
        try {
            dispatch(actions.updateChannelProcess())
            const updatedChannel = await channelsController.update(mapChannelToChannelDto(channel))
            const processedChannel = mapChannelDtoToChannel(updatedChannel, declarations)

            dispatch(actions.updateChannelSuccess())
            updateChannels(dispatch)(processedChannel, channel)
            const state = store.getState()
            const currentProjectId = selectCurrentProjectId(state)
            if (currentProjectId) {
                invalidateChannelsTags(dispatch, currentProjectId)
            }
            callback(channel)
        } catch (e) {
            handleHttpException(
                e,
                constants.UPDATE_CHANNEL_FAILED_MESSAGE,
                err => actions.updateChannelFailed(err),
                dispatch
            )
        }
    }
}

export function deleteChannel(projectId: string, channel: Channel, callback: () => void) {
    return async (dispatch: AppDispatch) => {
        try {
            dispatch(actions.deleteChannelProcess())
            await channelsController.delete(projectId, channel.Id)

            dispatch(actions.deleteChannelSuccess())
            updateChannels(dispatch)(undefined, channel)
            invalidateChannelsTags(dispatch, projectId)
            callback()
        } catch (e) {
            handleHttpException(
                e,
                constants.DELETE_CHANNEL_FAILED_MESSAGE,
                err => actions.deleteChannelFailed(err),
                dispatch
            )
        }
    }
}

export function getWebhooksPrefixes() {
    return async (dispatch: AppDispatch) => {
        try {
            dispatch(actions.getWebhooksPrefixesProcess())
            const webhooksPrefixes = await channelsController.getWebhooksPrefixes()
            dispatch(actions.getWebhooksPrefixesSuccess(webhooksPrefixes))
        } catch (e) {
            handleHttpException(
                e,
                constants.FETCH_CHANNELS_DECLARATIONS_FAILED_MESSAGE,
                err => actions.getWebhooksPrefixesFailed(err),
                dispatch
            )
        }
    }
}

export function getChannelsNames(projectId: string) {
    return async (dispatch: AppDispatch) => {
        try {
            dispatch(actions.getChannelsNamesProcess())
            const channelNames = await channelsController.getChannelNames(projectId)
            dispatch(actions.getChannelsNamesSuccess(channelNames))
        } catch (e) {
            handleHttpException(
                e,
                constants.FETCH_CHANNELS_FAILED_MESSAGE,
                err => actions.getChannelsNamesFailed(err),
                dispatch
            )
        }
    }
}

export function getChannelScenario() {
    return async (dispatch: AppDispatch) => {
        try {
            dispatch(actions.getChannelScenarioProcess())
            const scenario = await Controller.getChannelScenario
            dispatch(actions.getChannelScenarioSuccess(scenario))
        } catch (e) {
            handleHttpException(
                e,
                constants.FETCH_CHANNEL_SCENARIO_FAILED_MESSAGE,
                err => actions.getChannelScenarioFailed(err),
                dispatch
            )
        }
    }
}
